import { useTranslation } from "react-i18next";
import { useDocumentTitle } from "@mantine/hooks";
import { PAGE_TITLE } from "@constants/index";
import { Container, Title, Text, Stack, Paper, Divider, List, ThemeIcon } from "@mantine/core";
import { IconCheck } from "@tabler/icons-react";

export const PrivacyPolicy = () => {
  const { t } = useTranslation();
  useDocumentTitle(`${t("privacy-policy")} • ${PAGE_TITLE}`);

  return (
    <Container size="md" py="xl">
      <Paper withBorder p={{ base: "md", md: "xl" }} radius="md" shadow="sm">
        <Stack gap="lg">
          <div>
            <Title order={1} style={{ fontSize: "1.5rem", lineHeight: 1.2 }}>
              POLITIQUE DE CONFIDENTIALITÉ ET CONDITIONS D’UTILISATION – LA RUCHE HEALTH
            </Title>
            <Text size="sm" c="dimmed" mt="xs">
              (Pour les patients, les professionnels de santé et le personnel administratif de La Ruche Health)
            </Text>
            <Text size="sm" fw={500} mt={5}>Dernière mise à jour : Janvier 2026</Text>
          </div>
          
          <Divider />

          <Text size="sm" style={{ lineHeight: 1.6 }}>
            La présente politique définit les règles d'utilisation de la plateforme La Ruche Health et les
            modalités de protection des données personnelles et médicales. Elle s'applique à tous les
            utilisateurs : patients, professionnels de santé et personnel administratif autorisé.
          </Text>

          <section>
            <Title order={3} mb="sm">1. OBJET ET SERVICES</Title>
            <Text size="sm">
              La Ruche Health est une plateforme numérique facilitant l'accès aux soins via la
              téléconsultation, les visites à domicile et la gestion sécurisée des Dossiers Médicaux
              Électroniques (DME).
            </Text>
          </section>

          <section>
            <Title order={3} mb="sm">2. CONFORMITÉ LÉGALE</Title>
            <Text size="sm" mb="xs">La Ruche Health agit en stricte conformité avec :</Text>
            <List
              spacing="xs"
              size="sm"
              center
              icon={
                <ThemeIcon color="teal" size={20} radius="xl">
                  <IconCheck size={12} />
                </ThemeIcon>
              }
            >
              <List.Item>La Loi ivoirienne n°2013-450 sur la protection des données à caractère personnel.</List.Item>
              <List.Item>Le Code de la santé publique ivoirien.</List.Item>
              <List.Item>Les bonnes pratiques internationales (RGPD) pour les flux de données hors zone CEDEAO.</List.Item>
            </List>
          </section>

          <section>
            <Title order={3} mb="sm">3. ACCÈS AUX DONNÉES : QUI PEUT VOIR VOS INFORMATIONS ?</Title>
            <Text size="sm" mb="xs">L'accès aux données est strictement limité et hautement sécurisé :</Text>
            <Stack gap="xs">
              <Text size="sm"><strong>● Le Patient :</strong> Vous avez un accès total à vos propres données.</Text>
              <Text size="sm"><strong>● Les Professionnels de Santé :</strong> Uniquement les partenaires que vous autorisez explicitement pour votre prise en charge.</Text>
              <Text size="sm"><strong>● L’Équipe Administrative :</strong> Uniquement pour le suivi de qualité. Ils n'ont accès qu'aux données personnelles (contact, rdv) et n'ont aucun accès aux résultats d'analyses ou dossiers médicaux.</Text>
              <Text size="sm"><strong>● Sécurité :</strong> Tous les accès sont authentifiés (mot de passe + code PIN), tracés et soumis au secret médical.</Text>
            </Stack>
          </section>

          <section>
            <Title order={3} mb="sm">4. STOCKAGE ET HÉBERGEMENT : OÙ SONT VOS DONNÉES ?</Title>
            <Stack gap="xs">
              <Text size="sm"><strong>● Données Médicales :</strong> Hébergées exclusivement sur des serveurs sécurisés situés en Côte d’Ivoire.</Text>
              <Text size="sm"><strong>● Données Techniques :</strong> Les journaux de connexion et cookies peuvent être hébergés sur des clouds sécurisés en Europe ou aux États-Unis, sous accords conformes au RGPD.</Text>
              <Text size="sm"><strong>● Protection :</strong> Chiffrement TLS 1.2+ en transit et AES-256 au repos.</Text>
            </Stack>
          </section>

          <section>
            <Title order={3} mb="sm">5. CONSERVATION DES DONNÉES</Title>
            <Stack gap="xs">
              <Text size="sm"><strong>● Dossiers médicaux :</strong> Conservés pendant 13 ans après la dernière utilisation (durée légale).</Text>
              <Text size="sm"><strong>● Conversations Chatbot :</strong> Anonymisées après 12 mois.</Text>
            </Stack>
          </section>

          <section>
            <Title order={3} mb="sm">6. EXERCICE DES DROITS</Title>
            <Text size="sm" mb="xs">Vous pouvez demander l'accès, la rectification ou l'effacement de vos données à tout moment :</Text>
            <Stack gap="xs">
              <Text size="sm"><strong>● Application :</strong> Page Profil (« Contactez-nous »).</Text>
              <Text size="sm"><strong>● E-mail :</strong> <a href="mailto:partners@laruche.health">partners@laruche.health</a></Text>
              <Text size="sm"><strong>● WhatsApp :</strong> +225 05 04 78 36 82</Text>
            </Stack>
          </section>

          <Divider my="md" label="ANNEXE : FOIRE AUX QUESTIONS (FAQ) RÉFÉRENTIELLE" labelPosition="center" />

          <section>
            <Title order={4} mb="sm">I. INTELLIGENCE ARTIFICIELLE ET SÉCURITÉ MÉDICALE (KIKO)</Title>
            <Stack gap="md">
              <div>
                <Text size="sm" fw={700}>Q : Quels modèles d'IA utilisez-vous ?</Text>
                <Text size="sm">R : Nous utilisons une architecture multi-modèles incluant Gemini (Google) et GPT (OpenAI) pour le texte, ainsi qu'ElevenLabs et AssemblyAI pour les fonctions vocales.</Text>
              </div>
              <div>
                <Text size="sm" fw={700}>Q : Comment garantissez-vous que Kiko ne donne pas de fausses informations ?</Text>
                <Text size="sm">R : Grâce au système RAG (Retrieval-Augmented Generation). Kiko puise ses réponses dans une base de données de Protocoles Cliniques Locaux validés par l'UAO (Université Alassane Ouattara) et intègre les données de la CMU ivoirienne.</Text>
              </div>
              <div>
                <Text size="sm" fw={700}>Q : Kiko peut-il poser un diagnostic ?</Text>
                <Text size="sm">R : Non. Si vous demandez un diagnostic, notre "Safety Override" écrase la réponse de l'IA pour effectuer un screening et vous rediriger vers un professionnel humain.</Text>
              </div>
            </Stack>
          </section>

          <section>
            <Title order={4} mb="sm" mt="md">II. QUESTIONS PRATIQUES</Title>
            <Stack gap="md">
              <div>
                <Text size="sm" fw={700}>Q : Qui a accès à mes données ?</Text>
                <Text size="sm">R : Vous-même, vos médecins autorisés, et l'équipe administrative (pour la logistique uniquement, sans accès au contenu médical).</Text>
              </div>
              <div>
                <Text size="sm" fw={700}>Q : Où sont stockées mes données ?</Text>
                <Text size="sm">R : Vos données médicales restent en Côte d’Ivoire. Les données techniques de navigation sont sur des serveurs sécurisés internationaux conformes au RGPD.</Text>
              </div>
              <div>
                <Text size="sm" fw={700}>Q : Comment payer mes services ?</Text>
                <Text size="sm">R : Via Mobile Money (Orange, MTN, Wave, Moov, etc.), Carte Bancaire ou via nos assurances partenaires.</Text>
              </div>
              <div>
                <Text size="sm" fw={700}>Q : Que faire si la vidéo se coupe ?</Text>
                <Text size="sm">R : Rouvrez l'app et cliquez sur « Rejoindre la consultation ». Un contrôle est gratuit sous 14 jours pour le même motif.</Text>
              </div>
            </Stack>
          </section>
        </Stack>
      </Paper>
    </Container>
  );
};
